import React, { useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, Grid, Paper, Stack } from "@mui/material";
import Icon from "src/components/icon";
import styled from "styled-components";
import googleWorkspace from "src/assets/icons/google-workspace";
import { Helmet } from "react-helmet";
import request from "src/utils/request";
import { ToastContext } from "src/components/providers/toast-provider";
import { useNavigate } from "react-router-dom";
import fetchStatus from "src/utils/api/fetch-status";
import ReportIcon from "@mui/icons-material/Report";
import { useActiveIntegrations } from "src/scenes/dashboard/active-integrations-provider";

const H1 = styled.h1`
    font-size: 22px;
    margin: 0;
    font-weight: normal;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const H2 = styled.h2`
    font-size: 14px;
    font-weight: normal;
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const P = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin: 18px;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const P2 = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin: 0;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const PLabel = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin: 18px 18px 18px 0;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const CustomPaper = styled(Paper)`
    border-radius: 14px;
    width: 100%;
    cursor: pointer;
    transition: opacity 0.4s ease-out;
    position: static;
`;

const PermissionsIcon = styled(Icon)`
    background-color: #fff;
    border-radius: 16px;
    padding: 24px 12px 24px 12px;
    margin: 32px 0 32px 0;
`;

const DisconnectButton = styled(Button)`
    width: 136px;
    height: 36px;
`;

const SettingsGoogle = () => {
    const [settings] = React.useState(undefined);
    const [disconnectSelected, isDisconnectSelected] = React.useState(false);
    const navigate = useNavigate();
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const [centrallyManagedIntegrations, setCentrallyManagedIntegrations] = useState(false);
    const { activeIntegrations: userIntegrations } = useActiveIntegrations();

    React.useEffect(() => {
        if (userIntegrations.status === fetchStatus.SUCCESS) {
            const googleIntegration = userIntegrations.data.integrations.find(
                (item) => item.name === "google"
            );
            if (googleIntegration) {
                setCentrallyManagedIntegrations(!!googleIntegration.centrallyManaged);
            }
        }
    }, [userIntegrations.status]);

    const handleDisconnectChange = (event) => {
        isDisconnectSelected(event.target.checked);
    };

    const handleDisconnect = async () => {
        try {
            await request("/api/google/integration", { method: "DELETE" });
            userIntegrations.refetch();
            setMessage("Successfully disconnected Google integration. We are sad to see you go..");
            setSeverity("success");
            setIsToastTriggered(true);
            navigate("/integrations");
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            setMessage("Failed to disconnect Google. Please try again.");
            setSeverity("warning");
            setIsToastTriggered(true);
        }
        return undefined;
    };

    return (
        <>
            <Helmet>
                <title>Your Google settings</title>
            </Helmet>
            <Grid container justifyContent="center" mb={4}>
                <Stack
                    direction="column"
                    alignItems="flex-start"
                    style={{ height: "100%", width: "70%" }}
                >
                    <Stack direction="row" alignItems="center">
                        <PermissionsIcon svg={googleWorkspace} width="100px" height="127" />
                        <Stack direction="column" ml={2}>
                            <H1>
                                Google Settings
                                {centrallyManagedIntegrations && <H1>(Centrally Managed)</H1>}
                            </H1>
                            <H2>settings for your Google integration</H2>
                        </Stack>
                    </Stack>
                    {settings && settings.invalid === true && (
                        <CustomPaper style={{ marginBottom: "14px", padding: "7px" }}>
                            <Stack direction="row" alignItems="center">
                                <Box
                                    style={{
                                        backgroundColor: "darkorange",
                                        borderRadius: "46px",
                                        margin: "18px",
                                        height: "46px",
                                        width: "46px",
                                    }}
                                >
                                    <ReportIcon style={{ margin: "11px", color: "#303030" }} />
                                </Box>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    style={{ width: "88%" }}
                                >
                                    <Stack direction="column">
                                        <P2 style={{ fontSize: "14px", marginBottom: "8px" }}>
                                            <strong>
                                                {settings.invalidationError} for{" "}
                                                {settings.googleUserId}
                                            </strong>
                                        </P2>
                                        <P2>{settings.invalidationReason}</P2>
                                        <P2>
                                            You might be viewing partial or incorrect data.
                                            Reconnect your Google account.
                                        </P2>
                                    </Stack>
                                    {/* <Button onClick={handleReconnect}>Reconnect Google</Button> */}
                                </Stack>
                            </Stack>
                        </CustomPaper>
                    )}
                    <P style={{ fontSize: "16px", marginBottom: 0 }}>Disconnect Google</P>
                    <Stack
                        direction="row"
                        alignItems="center"
                        style={{ width: "100%", padding: "0px 16px" }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={disconnectSelected}
                                    onChange={handleDisconnectChange}
                                />
                            }
                            label={
                                <PLabel>
                                    Disconnect integration Adadot integration with Google and remove
                                    data.
                                </PLabel>
                            }
                        />
                        {disconnectSelected && (
                            <DisconnectButton variant="contained" onClick={handleDisconnect}>
                                Disconnect
                            </DisconnectButton>
                        )}
                    </Stack>
                </Stack>
            </Grid>
        </>
    );
};

export default SettingsGoogle;
